import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Download, ShieldCheck, Eye, Send, Ban, Pencil } from "lucide-react";
import api, { formatApiError, downloadOfferPdf } from "@/lib/api";
import { Loader } from "@/components/shared/Loader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ADMIN_STAGES } from "@/lib/constants";
import { SearchSelect } from "@/components/shared/SearchSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
const FORM_GROUPS = [
  ["Personal", [["first_name", "First Name"], ["middle_name", "Middle Name"], ["last_name", "Last Name"], ["dob", "Date of Birth"], ["gender", "Gender"], ["nationality", "Nationality"], ["country_of_residence", "Country of Residence"], ["mobile", "Mobile"], ["email", "Email"], ["address", "Address"], ["city", "City"], ["state", "State/Province"], ["country", "Country"], ["postal_code", "Postal Code"]]],
  ["Identification", [["nric_fin", "NRIC / FIN"], ["id_type", "ID Type"], ["id_expiry", "ID Expiry"], ["passport_no", "Passport No"], ["passport_country", "Passport Country"], ["passport_issue", "Passport Issue"], ["passport_expiry", "Passport Expiry"]]],
  ["Professional", [["current_title", "Current Title"], ["current_employer", "Current Employer"], ["total_experience", "Total Experience"], ["hotel_experience", "Hospitality Experience"], ["current_salary", "Current Salary"], ["expected_salary", "Expected Salary"], ["notice_period", "Notice Period"]]],
  ["Education", [["highest_qualification", "Highest Qualification"], ["institution", "Institution"], ["edu_country", "Country"], ["year_completed", "Year Completed"], ["specialization", "Specialization"]]],
  ["Job Preferences", [["pref_department", "Preferred Department"], ["pref_position", "Preferred Position"], ["pref_location", "Preferred Location"], ["pref_employment", "Employment Type"], ["available_from", "Available From"]]],
  ["Work Pass / Immigration", [["in_singapore", "Currently in Singapore"], ["holds_pass", "Holds Work Pass"], ["pass_type", "Pass Type"], ["pass_number", "Pass/FIN Number"], ["pass_expiry", "Pass Expiry"], ["worked_in_sg", "Worked in SG Before"], ["prev_sg_employer", "Prev SG Employer"], ["prev_sg_position", "Prev SG Position"], ["prev_sg_period", "Prev SG Period"]]],
];
const val = (fd, k) => (fd && fd[k] !== undefined && fd[k] !== null && fd[k] !== "" ? String(fd[k]) : "N/A");
const COMPLIANCE_STATUSES = ["pending", "in_progress", "complete", "failed"];

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [complianceTemplates, setComplianceTemplates] = useState([]);
  const [statusNote, setStatusNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [offerOpen, setOfferOpen] = useState(false);
  const [selSalary, setSelSalary] = useState("");
  const [selMedical, setSelMedical] = useState("");
  const [editForm, setEditForm] = useState(null);
  const sgToday = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/applications/${id}`);
    setApp(data);
    setNewStatus(data.status);
    setSelSalary(data.offer_prep?.salary || "");
    setSelMedical(data.offer_prep?.medical_date || "");
  }, [id]);

  useEffect(() => {
    load().catch(() => navigate("/admin/applications"));
    api.get("/admin/compliance-templates").then((r) => setComplianceTemplates(r.data));
  }, [id, load, navigate]);

  if (!app) return <Loader />;
  const STAGES = app.admin_stages || ADMIN_STAGES;

  const offerAction = async (o, action, msg) => {
    try {
      await api.post(`/admin/offers/${o.id}/${action}`);
      toast.success(msg);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const updateStatus = async () => {
    const body = { status: newStatus, note: statusNote };
    if (newStatus === "Selected") {
      if (!/^\d+(\.\d{1,2})?$/.test(selSalary.trim())) return toast.error("Enter the proposed monthly salary (SGD)");
      if (!selMedical || selMedical < sgToday) return toast.error("Select a medical examination date (today or later)");
      Object.assign(body, { salary: selSalary.trim(), medical_date: selMedical });
    }
    try {
      await api.patch(`/admin/applications/${id}/status`, body);
      toast.success("Status updated · candidate notified");
      setStatusNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const viewDoc = async (doc) => {
    try {
      const res = await api.get(`/candidate/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };

  const assignCompliance = async (templateId) => {
    try {
      await api.post("/admin/compliance/assign", { application_id: id, template_id: templateId });
      toast.success("Checklist assigned");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const updateComplianceItem = async (itemId, status) => {
    try {
      await api.patch(`/admin/compliance/${id}/item`, { item_id: itemId, status });
      load();
    } catch { toast.error("Failed to update item"); }
  };

  return (
    <div>
      <button onClick={() => navigate("/admin/applications")} className="mb-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#94A3B8] hover:text-gold">
        <ArrowLeft size={14} /> Pipeline
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-serif text-3xl tracking-tight">{app.candidate_name}</h1>
        <StatusBadge status={app.status} />
      </div>
      <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-[#94A3B8]">{app.reference} · {app.job_title} · {app.application_path} path · {app.candidate_email}</p>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {/* Left: status + history + docs */}
        <div className="space-y-6 lg:col-span-2">
          {/* Status control */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <p className="eyebrow">Update Stage</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <SearchSelect value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="app-status-select" className="rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm focus:border-gold focus:outline-none">
                {STAGES.map((s) => <option key={s}>{s}</option>)}
              </SearchSelect>
              <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Note (optional)" data-testid="app-status-note" className="flex-1 rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm focus:border-gold focus:outline-none" />
              <button onClick={updateStatus} data-testid="app-status-update" className="rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">Update</button>
            </div>
            {newStatus === "Selected" && (
              <div className="mt-4 grid gap-3 rounded-md border border-gold/20 bg-gold/5 p-4 sm:grid-cols-2" data-testid="selected-prereq">
                <p className="font-sans text-xs text-gold sm:col-span-2">Candidate will be emailed the proposed salary and medical examination deadline before the offer letter.</p>
                <label className="font-sans text-xs text-[#94A3B8]">Monthly salary (SGD) *
                  <input inputMode="decimal" value={selSalary} onChange={(e) => setSelSalary(e.target.value)} placeholder="e.g. 3900" data-testid="selected-salary" className="mt-1 w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 text-sm text-[#F1F5F9] focus:border-gold focus:outline-none" />
                </label>
                <label className="font-sans text-xs text-[#94A3B8]">Medical examination by *
                  <input type="date" min={sgToday} value={selMedical} onChange={(e) => setSelMedical(e.target.value)} data-testid="selected-medical-date" className="mt-1 w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 text-sm text-[#F1F5F9] [color-scheme:dark] focus:border-gold focus:outline-none" />
                </label>
              </div>
            )}
            {app.offer_prep && <p className="mt-3 font-sans text-xs text-[#94A3B8]" data-testid="offer-prep-info">Pre-offer: SGD {app.offer_prep.salary}/month · medical by {new Date(`${app.offer_prep.medical_date}T00:00:00`).toLocaleDateString("en-SG", { dateStyle: "medium" })}</p>}
          </section>

          {/* Full application form */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <div className="flex items-center justify-between">
              <p className="eyebrow">Full Application</p>
              {!app.candidate_deleted && <button onClick={() => setEditForm({ ...(app.form_data || {}) })} data-testid="app-form-edit" className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Pencil size={13} /> Edit details</button>}
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-gold">{app.applicant_type || "—"}</p>
            {FORM_GROUPS.map(([title, fields]) => (
              <div key={title} className="mt-5">
                <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">{title}</p>
                <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {fields.map(([k, label]) => (
                    <div key={k} className="border-b border-white/5 py-1.5">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
                      <p className="font-sans text-sm text-[#F1F5F9]">{val(app.form_data, k)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-5">
              <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">Skills</p>
              {app.form_data?.skills?.length ? (
                <div className="flex flex-wrap gap-1.5">{app.form_data.skills.map((s) => <span key={s} className="rounded-full border border-white/10 px-2.5 py-0.5 font-sans text-xs text-[#94A3B8]">{s}</span>)}</div>
              ) : <p className="font-sans text-sm text-[#F1F5F9]">N/A</p>}
              {app.form_data?.other_skills ? <p className="mt-1 font-sans text-sm text-[#94A3B8]">Other: {app.form_data.other_skills}</p> : null}
            </div>
            <div className="mt-5">
              <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">Languages</p>
              {app.form_data?.languages?.filter((l) => l.language)?.length ? app.form_data.languages.filter((l) => l.language).map((l, i) => <p key={i} className="font-sans text-sm text-[#F1F5F9]">{l.language} — {l.proficiency}</p>) : <p className="font-sans text-sm text-[#F1F5F9]">N/A</p>}
            </div>
            <div className="mt-5">
              <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">Previous Experience</p>
              {app.form_data?.experiences?.filter((e) => e.hotel)?.length ? app.form_data.experiences.filter((e) => e.hotel).map((e, i) => <p key={i} className="font-sans text-sm text-[#F1F5F9]">{e.position} · {e.hotel} ({e.country}, {e.period}) — {e.responsibilities}</p>) : <p className="font-sans text-sm text-[#F1F5F9]">N/A</p>}
            </div>
            <div className="mt-5">
              <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">Additional Qualifications</p>
              {app.form_data?.qualifications?.filter((q) => q.qualification)?.length ? app.form_data.qualifications.filter((q) => q.qualification).map((q, i) => <p key={i} className="font-sans text-sm text-[#F1F5F9]">{q.qualification} · {q.institution} ({q.country}, {q.year})</p>) : <p className="font-sans text-sm text-[#F1F5F9]">N/A</p>}
            </div>
            <div className="mt-5">
              <p className="mb-2 font-sans text-xs uppercase tracking-widest text-[#94A3B8]">Screening Answers</p>
              {app.additional_answers && Object.keys(app.additional_answers).length ? Object.entries(app.additional_answers).map(([k, v]) => <p key={k} className="font-sans text-sm text-[#F1F5F9]">{k}: <span className="text-gold">{v}</span></p>) : <p className="font-sans text-sm text-[#F1F5F9]">N/A</p>}
            </div>
          </section>

          {/* History */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <p className="eyebrow">History</p>
            <div className="mt-4 space-y-3 border-l border-white/10 pl-5">
              {[...app.history].reverse().map((h, i) => (
                <div key={i} className="relative">
                  <span className="absolute -left-[23px] top-1.5 h-2 w-2 rounded-full bg-gold" />
                  <div className="flex items-center gap-2">
                    <StatusBadge status={h.status} />
                    <span className="font-mono text-[10px] text-[#94A3B8]">{new Date(h.at).toLocaleString()} · {h.by}</span>
                  </div>
                  {h.note && <p className="mt-1 font-sans text-sm text-[#94A3B8]">{h.note}</p>}
                </div>
              ))}
            </div>
          </section>

          {/* Documents */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <p className="eyebrow">Candidate Documents (private)</p>
            <div className="mt-4 space-y-2">
              {app.documents?.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0A0D14] px-4 py-2.5">
                  <span className="flex items-center gap-2 font-sans text-sm"><FileText size={15} className="text-gold" />{d.original_filename} <span className="text-xs text-[#94A3B8]">({d.doc_type} v{d.version})</span></span>
                  <button onClick={() => viewDoc(d)} data-testid={`app-doc-view-${d.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Eye size={13} /> View</button>
                </div>
              ))}
              {(!app.documents || app.documents.length === 0) && <p className="font-sans text-sm text-[#94A3B8]">No documents uploaded.</p>}
            </div>
          </section>
        </div>

        {/* Right: offer + compliance */}
        <div className="space-y-6">
          {/* Offer */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <p className="eyebrow flex items-center gap-2"><ShieldCheck size={13} /> Offer Letter</p>
            {app.offers?.length > 0 ? (
              <div className="mt-4 space-y-3">
                {app.offers.map((o) => (
                  <div key={o.id} className="rounded-md border border-white/5 bg-[#0A0D14] p-4">
                    <div className="flex items-center justify-between">
                      <StatusBadge status={o.status} />
                      <span className="font-mono text-[10px] text-[#94A3B8]">v{o.current_version}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => setOfferOpen(o)} data-testid={`offer-view-${o.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Eye size={13} /> View</button>
                      <button onClick={() => downloadOfferPdf(`/admin/offers/${o.id}/pdf`, `Offer_${app.candidate_name}_v${o.current_version}.pdf`).catch(() => toast.error("Download failed"))} data-testid={`offer-pdf-${o.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Download size={13} /> PDF</button>
                      {o.status === "Generated" && (
                        <button onClick={() => offerAction(o, "send", "Offer sent to candidate")} data-testid={`offer-send-${o.id}`} className="flex items-center gap-1.5 rounded-md bg-gold px-3 py-1.5 font-sans text-xs font-semibold text-[#0A0D14] hover:bg-gold-bright"><Send size={13} /> Send</button>
                      )}
                      {["Generated", "Sent"].includes(o.status) && (
                        <button onClick={() => window.confirm("Withdraw this offer? The candidate will see it as cancelled.") && offerAction(o, "withdraw", "Offer withdrawn")} data-testid={`offer-withdraw-${o.id}`} className="flex items-center gap-1.5 rounded-md border border-rose-500/40 px-3 py-1.5 font-sans text-xs text-rose-300 hover:bg-rose-500/10"><Ban size={13} /> Withdraw</button>
                      )}
                    </div>
                    {o.status === "Sent" && <p className="mt-2 font-sans text-[11px] text-[#94A3B8]">Awaiting candidate. You can still regenerate (new version, re-send required) or withdraw before acceptance.</p>}
                    {o.status === "Accepted" && <p className="mt-2 font-sans text-[11px] text-emerald-300">Accepted by candidate — locked.</p>}
                    <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">{o.versions.length} version(s) on record</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 font-sans text-sm text-[#94A3B8]">No offer generated yet.</p>
            )}
            {!app.offers?.some((o) => o.status === "Accepted") && <OfferBuilder key={app.offer_prep?.set_at || "none"} app={app} onGenerated={load} />}
          </section>

          {/* Compliance */}
          <section className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <p className="eyebrow">Compliance Checklist</p>
            {app.compliance ? (
              <div className="mt-4 space-y-2">
                <p className="font-sans text-xs text-[#94A3B8]">{app.compliance.template_name}</p>
                {app.compliance.items.map((it) => (
                  <div key={it.id} className="rounded-md border border-white/5 bg-[#0A0D14] p-3">
                    <p className="font-sans text-sm">{it.label}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {COMPLIANCE_STATUSES.map((s) => (
                        <button key={s} onClick={() => updateComplianceItem(it.id, s)} data-testid={`compliance-${it.id}-${s}`} className={`rounded-full border px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${it.status === s ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-[#94A3B8]"}`}>{s.replace("_", " ")}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4">
                <p className="font-sans text-sm text-[#94A3B8]">Assign a checklist:</p>
                <div className="mt-3 space-y-2">
                  {complianceTemplates.map((t) => (
                    <button key={t.id} onClick={() => assignCompliance(t.id)} data-testid={`assign-compliance-${t.id}`} className="w-full rounded-md border border-white/10 px-3 py-2 text-left font-sans text-sm text-[#94A3B8] hover:border-gold hover:text-gold">{t.name}</button>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Offer view dialog */}
      <Dialog open={!!offerOpen} onOpenChange={(o) => !o && setOfferOpen(false)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Offer Letter</DialogTitle><DialogDescription className="text-[#94A3B8]">Current version preview</DialogDescription></DialogHeader>
          {offerOpen && (
            <div>
              <div className="mb-4 flex items-center gap-2">
                <StatusBadge status={offerOpen.status} />
                <span className="font-mono text-[10px] text-[#94A3B8]">Version {offerOpen.current_version}</span>
              </div>
              <div className="rounded-md bg-white p-6 text-black" dangerouslySetInnerHTML={{ __html: offerOpen.versions.find((v) => v.version === offerOpen.current_version)?.html || "" }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {editForm && <EditFormDialog app={app} form={editForm} setForm={setEditForm} onSaved={() => { setEditForm(null); load(); }} />}
    </div>
  );
}

function EditFormDialog({ app, form, setForm, onSaved }) {
  const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 font-sans text-sm text-[#F1F5F9] focus:border-gold focus:outline-none";
  const save = async () => {
    const changed = Object.fromEntries(FORM_GROUPS.flatMap(([, f]) => f.map(([k]) => k))
      .filter((k) => (form[k] ?? "") !== (app.form_data?.[k] ?? "")).map((k) => [k, form[k] ?? ""]));
    if (!Object.keys(changed).length) return toast.info("No changes");
    try {
      await api.put(`/admin/applications/${app.id}/form`, { form_data: changed });
      toast.success(`Updated ${Object.keys(changed).length} field(s) · recorded in audit log`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit application details</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Correct anything the candidate entered wrongly. Every change is recorded in the audit log.</DialogDescription>
        </DialogHeader>
        {FORM_GROUPS.map(([title, fields]) => (
          <div key={title} className="mt-2">
            <p className="mb-2 font-sans text-xs uppercase tracking-widest text-gold">{title}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map(([k, label]) => (
                <label key={k} className="font-sans text-xs text-[#94A3B8]">{label}
                  <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-field-${k}`} className={`${cls} mt-1`} />
                </label>
              ))}
            </div>
          </div>
        ))}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setForm(null)} className="rounded-full border border-white/10 px-5 py-2 font-sans text-xs text-[#94A3B8]">Cancel</button>
          <button onClick={save} data-testid="edit-form-save" className="rounded-full bg-gold px-5 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14]">Save changes</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OfferBuilder({ app, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [salary, setSalary] = useState(app.offer_prep?.salary || "");
  const [medicalDate, setMedicalDate] = useState(app.offer_prep?.medical_date || "");
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Singapore" });
  const [preview, setPreview] = useState(null);
  const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 font-sans text-sm focus:border-gold focus:outline-none";
  const validSalary = () => {
    if (!/^\d+(\.\d{1,2})?$/.test(salary.trim())) { toast.error("Enter a valid monthly salary (SGD)"); return false; }
    if (!medicalDate || medicalDate < today) { toast.error("Select a medical examination date (today or later)"); return false; }
    return true;
  };

  const doPreview = async () => {
    if (!validSalary()) return;
    try {
      const { data } = await api.post("/admin/offers/preview", { application_id: app.id, salary: salary.trim(), medical_date: medicalDate });
      setPreview(data.html);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const doGenerate = async () => {
    if (!validSalary()) return;
    try {
      await api.post("/admin/offers/generate", { application_id: app.id, salary: salary.trim(), medical_date: medicalDate });
      toast.success("Offer generated (draft)");
      setOpen(false);
      onGenerated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="crm-generate-offer-btn" className="mt-4 w-full rounded-full border border-gold/40 px-4 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-gold hover:bg-gold hover:text-[#0A0D14]">
        Generate / Regenerate Offer
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Employment Letter</DialogTitle><DialogDescription className="text-[#94A3B8]">Position and location are taken from the job. Joining date follows work pass processing.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 font-sans text-sm">
              <p className="rounded-md border border-white/5 bg-[#0A0D14] px-3 py-2"><span className="block font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">Position</span><span data-testid="offer-position">{app.job_title}</span></p>
              <p className="rounded-md border border-white/5 bg-[#0A0D14] px-3 py-2"><span className="block font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">Location</span><span data-testid="offer-location">{app.location || "Singapore"}</span></p>
            </div>
            <label className="block font-sans text-xs text-[#94A3B8]">Monthly salary (SGD) <span className="text-gold">*</span></label>
            <input inputMode="decimal" placeholder="e.g. 3900" value={salary} onChange={(e) => setSalary(e.target.value)} className={cls} data-testid="merge-salary" />
            <label className="block font-sans text-xs text-[#94A3B8]">Medical examination by <span className="text-gold">*</span></label>
            <input type="date" min={today} value={medicalDate} onChange={(e) => setMedicalDate(e.target.value)} className={`${cls} [color-scheme:dark]`} data-testid="merge-medical-date" />
            <p className="font-sans text-[11px] text-[#94A3B8]">Signed digitally by John Williams (HR Manager) with company stamp.</p>
            <div className="flex gap-2">
              <button onClick={doPreview} data-testid="offer-preview-btn" className="flex-1 rounded-full border border-white/10 px-4 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#94A3B8] hover:border-gold hover:text-gold">Preview</button>
              <button onClick={doGenerate} data-testid="offer-generate-btn" className="flex-1 rounded-full bg-gold px-4 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">Generate</button>
            </div>
            {preview && <div className="rounded-md bg-white p-6 text-black" dangerouslySetInnerHTML={{ __html: preview }} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
